/**
 * Which streaming transcripts still have a socket streaming audio into them.
 *
 * The upload path supersedes the streaming transcripts a local recording session
 * produced (#2828, ADR-033) by deleting them — which is only ever correct for an
 * attempt that is *dead*. A transcript whose socket is still open keeps receiving
 * audio after the delete: the whisper session is not closed by a backend delete,
 * so it goes on buffering, finalizes, and writes a finished transcript into a
 * soft-deleted row. That is how 55 minutes of a production consult became
 * invisible (#3676).
 *
 * The recorder is the only thing that knows, so it registers each transcript
 * against the socket that carries it (the WebSocket `ready` message) and releases
 * it when it walks away from that socket. The registry is module-level on
 * purpose: the recorder lives in the appointment page while the recovery upload
 * lives in `App.vue`, so the answer has to cross component trees.
 *
 * Liveness is read off the socket's own `readyState` rather than trusting the
 * release calls alone. A missed release would otherwise mark a transcript live
 * forever and permanently block the supersede this exists to keep correct — the
 * failure mode is the one nothing would ever notice.
 */

/** The socket streaming into each transcript, by transcript id. */
const socketsByTranscriptId = new Map<string, WebSocket>()

/**
 * Whether audio can still reach this socket. A socket that is closing or closed
 * receives nothing more, so whisper finalizes what it has — the transcript is
 * then a completed attempt, which is exactly what a superseding upload replaces.
 */
function isStreaming(socket: WebSocket): boolean {
  return socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN
}

/** Drop entries whose socket has since closed, so the map cannot grow unbounded. */
function pruneClosedSockets(): void {
  for (const [transcriptId, socket] of socketsByTranscriptId) {
    if (!isStreaming(socket)) {
      socketsByTranscriptId.delete(transcriptId)
    }
  }
}

/**
 * Record that `socket` is streaming audio into `transcriptId`. Called from the
 * recorder's `ready` handler, which is where a socket first learns its transcript.
 */
export function markTranscriptStreaming(transcriptId: string, socket: WebSocket): void {
  pruneClosedSockets()
  socketsByTranscriptId.set(transcriptId, socket)
}

/**
 * Release every transcript registered against `socket` — the recorder has ended
 * or abandoned its streaming session, so nothing more will be written into them.
 */
export function releaseStreamingSocket(socket: WebSocket): void {
  for (const [transcriptId, registered] of socketsByTranscriptId) {
    if (registered === socket) {
      socketsByTranscriptId.delete(transcriptId)
    }
  }
}

/**
 * Whether a transcript is still being streamed into, and therefore off limits to
 * the supersede/delete path.
 */
export function isTranscriptStreaming(transcriptId: string): boolean {
  const socket = socketsByTranscriptId.get(transcriptId)
  if (!socket) {
    return false
  }
  if (isStreaming(socket)) {
    return true
  }
  socketsByTranscriptId.delete(transcriptId)
  return false
}

/** Clear the registry. For tests, which share module state across cases. */
export function resetStreamingTranscripts(): void {
  socketsByTranscriptId.clear()
}
